import Cookies from 'js-cookie';

/**
 * Cookie consent state.
 *
 * Everything non-essential on this site is off until the visitor says otherwise. That is not a
 * stylistic choice: GA4 assigns a persistent client ID in `_ga`, which puts it outside the PECR
 * "statistical purposes" exception added by the Data (Use and Access) Act 2025 (the ICO's
 * finalised guidance excludes connecting a visitor ID to site activity), so it needs consent.
 * Tawk.to is a third-party script setting its own cookies, so it does too.
 *
 * The region cookie deliberately is not gated here. Remembering a UK/US preference falls under
 * the "appearance" exception the same way a remembered language does, and the header's region
 * toggle is the "simple and free means to object" that the exception requires. Gating it would
 * break regional pricing for anyone who declines, which costs the visitor something and gains
 * them no privacy.
 */

export const CONSENT_COOKIE = 'ck_consent';

const COOKIE_EXPIRY_DAYS = 365;

/**
 * Bumping this re-asks everyone. It exists because consent is per-purpose: if a category is
 * added, silently inheriting a yes that was given for analytics would be consent the visitor
 * never gave. Bump it whenever an existing category's purpose materially changes.
 *
 * Adding a category does not need a bump on its own, because `readConsent` maps any category a
 * stored record doesn't mention to false. That is how ADVERTISING was added in October 2026: a
 * visitor who accepted analytics and chat under version 1 reads back as advertising: false, so
 * nothing is inherited. Leaving the version alone also avoids re-asking every existing visitor
 * and member, and avoids a lockstep deploy -- the member app and the inline consent-mode script
 * in gatsby-ssr.js both check `version === 1`.
 */
export const CONSENT_VERSION = 1;

export const ANALYTICS = 'analytics';
export const CHAT = 'chat';
// Meta Pixel, on this site only. Never loaded in the member app, never given member data.
export const ADVERTISING = 'advertising';

export const CATEGORIES = [ANALYTICS, CHAT, ADVERTISING];

const DENIED = { [ANALYTICS]: false, [CHAT]: false, [ADVERTISING]: false };
const GRANTED = { [ANALYTICS]: true, [CHAT]: true, [ADVERTISING]: true };

const subscribers = new Set();

const isBrowser = () => typeof window !== 'undefined';

/**
 * The domain the consent cookie is written on.
 *
 * `.careerskitchen.com` so that one decision covers both this site and the member app at
 * app.careerskitchen.com. Members move between the two constantly, and asking twice -- or
 * worse, tracking someone in the app who refused here -- is the failure this exists to stop.
 *
 * Anything else gets a host-only cookie. A dotted cookie on `.herokuapp.com` is rejected under
 * the public-suffix rules and would silently fail to persist, leaving the banner on screen
 * forever in preview builds and on localhost.
 */
export const cookieDomain = () => {
  if (!isBrowser()) return undefined;

  const { hostname } = window.location;

  return hostname === 'careerskitchen.com' || hostname.endsWith('.careerskitchen.com')
    ? '.careerskitchen.com'
    : undefined;
};

const cookieOptions = () => {
  const domain = cookieDomain();

  return {
    expires: COOKIE_EXPIRY_DAYS,
    path: '/',
    sameSite: 'Lax',
    ...(domain ? { domain } : {}),
    /* Secure would make the cookie undeliverable over http, so `gatsby develop` on localhost
       would never persist a choice and the banner would reappear on every page. */
    secure: isBrowser() && window.location.protocol === 'https:',
  };
};

/**
 * Moves a pre-existing host-only record onto the parent domain.
 *
 * Every decision recorded before the shared cookie shipped is host-only on www. Left alone it
 * would shadow the shared one -- same name, more specific domain, and js-cookie returns only
 * the first match -- so a visitor who later decided in the app would appear here not to have
 * decided, or to have decided something else. The payload is carried across unchanged rather
 * than re-recorded, so the original timestamp survives.
 *
 * Safe to call on every page load: with no cookie, or with only the shared one, it does
 * nothing.
 */
export const migrateConsentDomain = () => {
  if (!isBrowser()) return;

  const domain = cookieDomain();
  if (!domain) return;

  const raw = Cookies.get(CONSENT_COOKIE);
  if (!raw) return;

  Cookies.remove(CONSENT_COOKIE, { path: '/' });

  /* Something still readable means a shared cookie was underneath the host-only one all
     along, and it is the one to keep. */
  if (Cookies.get(CONSENT_COOKIE)) return;

  Cookies.set(CONSENT_COOKIE, raw, cookieOptions());
};

/**
 * Reads the stored record, or null when there isn't a usable one.
 *
 * A record from an older version is treated as absent rather than migrated, which re-shows the
 * banner. Same for anything unparseable — a corrupt cookie should fail to "ask again", never to
 * "assume yes".
 */
export const readConsent = () => {
  if (!isBrowser()) return null;

  const raw = Cookies.get(CONSENT_COOKIE);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);

    if (!parsed || parsed.version !== CONSENT_VERSION) return null;

    return {
      version: parsed.version,
      timestamp: parsed.timestamp,
      categories: CATEGORIES.reduce(
        (acc, category) => ({
          ...acc,
          [category]: parsed.categories?.[category] === true,
        }),
        {}
      ),
    };
  } catch (error) {
    return null;
  }
};

export const hasDecided = () => readConsent() !== null;

/**
 * Whether a category is allowed. Absent record means no, so every caller can ask this without
 * first checking whether a decision exists.
 */
export const isAllowed = (category) => {
  const record = readConsent();

  return record ? record.categories[category] === true : false;
};

export const getCategories = () => {
  const record = readConsent();

  return record ? record.categories : { ...DENIED };
};

export const subscribe = (callback) => {
  subscribers.add(callback);

  return () => subscribers.delete(callback);
};

/**
 * Tells Google what the visitor decided.
 *
 * The three Google advertising consent types stay denied unconditionally. The ADVERTISING
 * category covers Meta's pixel only; the site runs no Google Ads and no Google remarketing, so
 * there is nothing to grant them for, and granting them "just in case" would be asserting a
 * consent the banner never asked for.
 */
const updateGoogleConsent = (categories) => {
  if (!isBrowser() || typeof window.gtag !== 'function') return;

  window.gtag('consent', 'update', {
    analytics_storage: categories[ANALYTICS] ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

/**
 * Clears cookies a category is no longer allowed to keep.
 *
 * Withdrawal has to actually remove what was set, otherwise "turn analytics off" leaves the
 * visitor identified by a `_ga` client ID that simply stops being added to. Google's own cookies
 * are host-only on the apex domain and dotted on the parent, so both spellings are attempted.
 */
const clearCookiesFor = (category) => {
  if (!isBrowser()) return;

  // Tawk's visitor cookies are `twk_uuid_<property>` and `TawkConnectionTime`, observed on
  // production on 24 Sept 2026 -- not `__tawk*`, which matched nothing. `twk_uuid_` is a persistent
  // visitor ID, so withdrawing chat consent left the visitor identifiable. `__tawk` stays in case
  // older widget builds still use it. Mirrors the web app (careerskitchen-web-app PR #315).
  const prefixes = {
    [ANALYTICS]: ['_ga', '_gid', '_gat'],
    [CHAT]: ['__tawk', 'twk_', 'TawkConnectionTime'],
    // Meta's first-party browser and click IDs. Both are set on the parent domain by fbevents.js.
    [ADVERTISING]: ['_fbp', '_fbc'],
  };
  const targets = prefixes[category] || [];
  const { hostname } = window.location;
  const parent = hostname.replace(/^www\./, '');

  document.cookie
    .split(';')
    .map((entry) => entry.split('=')[0].trim())
    .filter((name) => targets.some((prefix) => name.startsWith(prefix)))
    .forEach((name) => {
      Cookies.remove(name, { path: '/' });
      Cookies.remove(name, { path: '/', domain: `.${parent}` });
      Cookies.remove(name, { path: '/', domain: parent });
    });

  // Tawk also keeps `twk_token_<property>` in localStorage -- a JWT whose claims include the
  // visitor ID -- plus `twk_<property>`, its server route. Both outlived chat withdrawal on
  // production on 24 Sept 2026. Storage can throw when blocked (Safari private mode), and a failed
  // sweep must never stop the consent change taking effect. Mirrors careerskitchen-web-app #316.
  const storagePrefixes = { [CHAT]: ['twk_'] };
  const storageTargets = storagePrefixes[category] || [];

  if (!storageTargets.length) return;

  try {
    Object.keys(window.localStorage)
      .filter((key) => storageTargets.some((prefix) => key.startsWith(prefix)))
      .forEach((key) => window.localStorage.removeItem(key));
  } catch (error) {
    // Nothing to clear if storage is unavailable.
  }
};

/**
 * Records a decision and notifies everything that depends on it.
 *
 * Writing is the only way consent changes, so this is also where the side effects belong: Google
 * is told, newly-withdrawn cookies are cleared, and subscribers re-render.
 */
export const setConsent = (categories) => {
  if (!isBrowser()) return;

  const previous = getCategories();

  const next = CATEGORIES.reduce(
    (acc, category) => ({ ...acc, [category]: categories[category] === true }),
    {}
  );

  Cookies.set(
    CONSENT_COOKIE,
    JSON.stringify({
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      categories: next,
    }),
    cookieOptions()
  );

  /* A host-only record left from before the shared cookie shipped would shadow the one just
     written, so the visitor's new choice would appear not to have taken. Harmless when it was
     never there. */
  if (cookieDomain()) Cookies.remove(CONSENT_COOKIE, { path: '/' });

  updateGoogleConsent(next);

  CATEGORIES.filter((category) => previous[category] && !next[category]).forEach(
    clearCookiesFor
  );

  subscribers.forEach((callback) => callback(next));
};

export const acceptAll = () => setConsent(GRANTED);

export const rejectAll = () => setConsent(DENIED);
